import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { APP_VERSION } from "../version";

// Hilfsfunktion: Läuft die App als installierte PWA?
function isPWA() {
  return (
    window.matchMedia('(display-mode: standalone)').matches || // Android/Desktop
    window.navigator.standalone === true                       // iOS Safari
  );
}

export default function Home() {
  const navigate = useNavigate();
  const featuresRef = useRef([]);
  const imageContainerRef = useRef(null);
  const [activeImageIndex, setActiveImageIndex] = useState(0);

// PWA-Weiterleitung: Nur wenn als installierte PWA geöffnet
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      navigate('/main-hub', { replace: true }); // Eingeloggt (Browser & PWA) → direkt zur App
      return;
    }
    if (isPWA()) {
      navigate('/login', { replace: true });    // PWA + nicht eingeloggt → Anmelde-Seite
    }
    // Browser + nicht eingeloggt → Landing Page normal anzeigen
  }, []); // eslint-disable-line

  // Einfacher Scroll-Handler basierend auf Container-Position
  const updateActiveImage = useCallback(() => {
    const container = imageContainerRef.current;
    if (!container) return;

    const containerRect = container.getBoundingClientRect();
    const containerTop = containerRect.top;
    const viewportHeight = window.innerHeight;
    
    // Berechne wie weit wir durch den Container gescrollt haben
    const scrollProgress = -containerTop / (containerRect.height - viewportHeight);
    
    // 8 Bilder, also Index 0-7
    const totalImages = 8;
    const newIndex = Math.min(
      Math.max(0, Math.floor(scrollProgress * totalImages)),
      totalImages - 1
    );
    
    if (newIndex !== activeImageIndex && newIndex >= 0 && newIndex < totalImages) {
      setActiveImageIndex(newIndex);
    }
  }, [activeImageIndex]);

  useEffect(() => {
    // Observer für Features (normale Animation)
    const featuresObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.style.opacity = "1";
            entry.target.style.transform = "translateY(0)";
          }
        });
      },
      { threshold: 0.1 }
    );

    featuresRef.current.forEach((ref) => {
      if (ref) featuresObserver.observe(ref);
    });

    // Scroll listener
    let rafId = null;
    const handleScroll = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        updateActiveImage();
        rafId = null;
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Initial

    return () => {
      featuresObserver.disconnect();
      window.removeEventListener('scroll', handleScroll);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [updateActiveImage]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-emerald-900 to-teal-900 relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-emerald-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-teal-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-cyan-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-4000"></div>
      </div>

      {/* Hero Section */}
      <div className="relative container mx-auto px-6 py-12">
        <div className="flex flex-col items-center justify-center min-h-[90vh] text-center">
          {/* Logo - Rund und animiert */}
          <div className="mb-8 animate-fade-in">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-emerald-400 to-teal-400 rounded-full blur-2xl opacity-50 animate-pulse"></div>
              <img 
                src="/burningteam-logo.png" 
                alt="BurningTeam Logo" 
                className="relative h-40 w-40 rounded-full mx-auto shadow-2xl border-4 border-white/20 hover:scale-110 transition-transform duration-500"
              />
            </div>
          </div>

          {/* Beta Badge */}
          <div className="mb-6 animate-fade-in animation-delay-200">
            <span className="inline-block px-4 py-2 bg-amber-400 text-amber-900 text-sm font-bold rounded-full shadow-lg">
              🔥 Beta Version
            </span>
          </div>

          {/* Motivierender Text */}
          <div className="mb-8 animate-fade-in animation-delay-400 max-w-3xl">
            <h1 className="text-5xl md:text-6xl font-extrabold text-white mb-6 leading-tight">
              Du brennst für dein Team?
            </h1>
            <p className="text-2xl md:text-3xl text-emerald-200 font-semibold mb-6">
              Wir helfen dir, diese Energie richtig zu nutzen!
            </p>
            
            {/* Zusätzliche CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 mb-8 justify-center">
              <Link
                to="/login"
                className="px-8 py-3 bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white rounded-lg font-semibold text-lg shadow-lg border-2 border-white/30 transition-all duration-300 transform hover:scale-105"
              >
                Anmelden
              </Link>
              <Link
                to="/register"
                className="px-8 py-3 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white rounded-lg font-semibold text-lg shadow-lg transition-all duration-300 transform hover:scale-105"
              >
                Registrieren
              </Link>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 md:p-8 border border-white/20 shadow-2xl">
              <p className="text-lg md:text-xl text-white leading-relaxed mb-4">
                <span className="font-bold text-emerald-300">BurningTeam</span> ist das Tool, das dir zeigt, wie leicht Teamaufbau und smarte Organisation sein können.
              </p>
              <p className="text-lg md:text-xl text-white leading-relaxed mb-4">
                Alles wird <span className="font-bold text-teal-300">übersichtlicher, schneller und einfach effektiver</span> – damit dein Team auch angezündet wird und wachsen kann, während du den Kopf für das Wesentliche frei hast.
              </p>
              <p className="text-xl md:text-2xl font-bold text-emerald-300 mt-6">
                🚀 Registriere dich jetzt und leg los!
              </p>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 mb-12 animate-fade-in animation-delay-600">
            <Link
              to="/register"
              className="group px-10 py-5 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white rounded-xl font-bold text-xl shadow-2xl transition-all duration-300 transform hover:scale-110 hover:-translate-y-1 relative overflow-hidden"
            >
              <span className="relative z-10">Jetzt durchstarten! 🔥</span>
              <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 transform -skew-x-12 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"></div>
            </Link>
          </div>
        </div>
      </div>

      {/* "Kennst du das?" Problem-Section mit Scroll-Animationen */}
      <div className="relative py-20 bg-gradient-to-b from-slate-900/30 to-transparent">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            {/* Überschrift */}
            <div 
              ref={(el) => (featuresRef.current[10] = el)}
              className="opacity-0 transform translate-y-10 transition-all duration-700 ease-out text-center mb-16"
            >
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                Kennst du das? 🤔
              </h2>
            </div>

            {/* Bilder mit Fade-In/Out Animation */}
            <div ref={imageContainerRef} className="space-y-8">
              {[
                { src: "/landing-1.png", alt: "Teampartner Thematiken" },
                { src: "/landing-2.png", alt: "Besprochene Themen" },
                { src: "/landing-3.png", alt: "Interessen und Aufgaben" },
                { src: "/landing-4.png", alt: "Wichtige Notizen merken" },
                { src: "/landing-5.png", alt: "Überblick verlieren" },
                { src: "/landing-6.png", alt: "Vorratsschrank" },
                { src: "/landing-7.png", alt: "Schulden Übersicht" },
                { src: "/landing-8.png", alt: "Perfekte Übersicht" }
              ].map((image, index) => {
                const isActive = activeImageIndex === index;
                const isPast = activeImageIndex > index;
                const isFuture = activeImageIndex < index;
                
                return (
                  <div
                    key={index}
                    data-index={index}
                    className="min-h-[60vh] flex items-center justify-center py-8"
                  >
                    <div 
                      className={`relative max-w-[95%] md:max-w-[85%] transition-all duration-500 ease-out ${
                        isActive 
                          ? 'opacity-100 scale-100 blur-0' 
                          : isPast 
                            ? 'opacity-0 scale-95 blur-sm' 
                            : isFuture
                              ? 'opacity-0 scale-90 blur-md'
                              : 'opacity-0 scale-90'
                      }`}
                    >
                      {/* Glow Effect */}
                      <div className={`absolute -inset-4 bg-gradient-to-r from-emerald-500/30 to-teal-500/30 rounded-3xl blur-xl transition-opacity duration-500 ${isActive ? 'opacity-100' : 'opacity-0'}`}></div>
                      
                      {/* Image Container */}
                      <div className="relative overflow-hidden rounded-2xl shadow-2xl border-2 border-white/20">
                        <img 
                          src={image.src} 
                          alt={image.alt}
                          className="w-full h-auto"
                          loading="lazy"
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Finaler CTA Block - 3 Vorschläge zur Auswahl */}
            <div 
              ref={(el) => (featuresRef.current[11] = el)}
              className="opacity-0 transform translate-y-10 transition-all duration-700 ease-out mt-8"
            >
              {/* Option 1: Großer animierter Button mit Puls-Effekt */}
              <div className="text-center py-12">
                <div className="relative inline-block">
                  {/* Pulsierender Ring-Effekt */}
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-500 animate-ping opacity-20"></div>
                  <div className="absolute -inset-2 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-500 blur-lg opacity-50 animate-pulse"></div>
                  
                  <Link
                    to="/register"
                    className="relative inline-flex items-center gap-3 px-12 py-6 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-white rounded-2xl font-bold text-xl md:text-2xl shadow-2xl transition-all duration-300 transform hover:scale-110 hover:-translate-y-2 group"
                  >
                    <span>Jetzt kostenlos durchstarten</span>
                    <svg className="w-8 h-8 group-hover:translate-x-2 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </Link>
                </div>
                
                <p className="mt-6 text-emerald-200 text-lg">
                  Keine Kreditkarte erforderlich • Sofort loslegen
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="relative bg-gradient-to-b from-transparent to-slate-900/50 py-20">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl md:text-5xl font-bold text-center text-white mb-4 animate-fade-in">
            Was macht <span className="text-emerald-400">BurningTeam</span> besonders?
          </h2>
          <p className="text-xl text-center text-emerald-200 mb-16 animate-fade-in animation-delay-200">
            Die perfekte Lösung für dein wachsendes Team
          </p>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {[
              {
                icon: (
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                ),
                color: "from-emerald-500 to-teal-500",
                title: "Zielplanungskalender",
                description: "Jahres-, Monats- und Wochenziele an einem Ort. Inkl. To-Dos, Prioritäten und Push-Erinnerungen – damit dir kein wichtiger Termin mehr durchrutscht."
              },
              {
                icon: (
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                ),
                color: "from-blue-500 to-cyan-500",
                title: "Team- & Kontaktorganisation",
                description: "Kaltkontakte, Teampartner & Kunden managen, Anrufliste abarbeiten, Termine koordinieren. Dein Team wächst – und du behältst die Kontrolle."
              },
              {
                icon: (
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                ),
                color: "from-purple-500 to-pink-500",
                title: "Warenverwaltung & Reports",
                description: "Produkte, Bestände, Rechnungen und Quartalsberichte an einem Ort. Mit Analysen, die dir helfen, bessere Entscheidungen zu treffen."
              }
            ].map((feature, index) => (
              <div
                key={index}
                ref={(el) => (featuresRef.current[index] = el)}
                className="opacity-0 transform translate-y-20 transition-all duration-700 ease-out"
                style={{ transitionDelay: `${index * 200}ms` }}
              >
                <div className="group bg-white/10 backdrop-blur-md rounded-2xl p-8 shadow-2xl border border-white/20 hover:bg-white/20 transition-all duration-300 hover:scale-105 hover:-translate-y-2 h-full">
                  <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${feature.color} flex items-center justify-center mb-6 mx-auto group-hover:scale-110 group-hover:rotate-6 transition-all duration-300 shadow-lg text-white`}>
                    {feature.icon}
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-4 text-center group-hover:text-emerald-300 transition-colors">
                    {feature.title}
                  </h3>
                  <p className="text-emerald-100 text-center leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Beta Notice */}
      <div className="relative py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto bg-gradient-to-r from-amber-500/20 to-orange-500/20 backdrop-blur-sm border-2 border-amber-400/50 rounded-2xl p-8 shadow-2xl">
            <div className="flex items-start">
              <svg className="w-8 h-8 text-amber-300 mr-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="text-left">
                <h4 className="text-2xl font-bold text-amber-200 mb-3">Beta-Version – Sei von Anfang an dabei!</h4>
                <p className="text-lg text-white leading-relaxed mb-4">
                  Diese App befindet sich in der Beta-Phase und wird kontinuierlich verbessert. 
                  Dein Feedback hilft uns, BurningTeam noch besser zu machen!
                </p>
                <p className="text-white">
                  Fehler oder Verbesserungsvorschläge? Schreib uns:{" "}
                  <a href="mailto:support@burningteam.de" className="text-emerald-300 font-bold underline hover:text-emerald-200 transition-colors">
                    support@burningteam.de
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="relative bg-slate-900/80 backdrop-blur-sm border-t border-white/10 text-slate-300 py-10">
        <div className="container mx-auto px-6 text-center">
          <div className="mb-4">
            <img 
              src="/burningteam-logo.png" 
              alt="BurningTeam" 
              className="h-12 w-12 rounded-full mx-auto shadow-lg"
            />
          </div>
          <p className="text-lg font-semibold text-white mb-2">
            BurningTeam - Organisationshilfe mit Zielplanungskalender &amp; Warenverwaltung
          </p>
          <p className="text-sm text-slate-400 mb-4">
            © {new Date().getFullYear()} Alle Rechte vorbehalten
          </p>
          <p className="text-xs text-slate-500">
            Diese App wird privat entwickelt und bereitgestellt. Alle Angaben erfolgen ohne Gewähr.
          </p>
          <p className="text-xs text-slate-600 mt-2">
            v{APP_VERSION}
          </p>
        </div>
      </footer>

      <style jsx>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes blob {
          0%, 100% {
            transform: translate(0, 0) scale(1);
          }
          25% {
            transform: translate(20px, -50px) scale(1.1);
          }
          50% {
            transform: translate(-20px, 20px) scale(0.9);
          }
          75% {
            transform: translate(50px, 50px) scale(1.05);
          }
        }

        .animate-fade-in {
          animation: fade-in 0.8s ease-out forwards;
        }

        .animation-delay-200 {
          animation-delay: 0.2s;
        }

        .animation-delay-400 {
          animation-delay: 0.4s;
        }

        .animation-delay-600 {
          animation-delay: 0.6s;
        }

        .animate-blob {
          animation: blob 7s infinite;
        }

        .animation-delay-2000 {
          animation-delay: 2s;
        }

        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
    </div>
  );
}
